import { niveauDe, type Avis } from "../api/hooks";

/**
 * §11.18 / §11.19 — LE CANAL D'AVIS DE CHAQUE MOTEUR, RENDU VISIBLE.
 *
 * Une exception avalée par un `try` d'appelant faisait présenter le dossier
 * comme VIDE, et la vraie cause restait invisible. Un refus DOIT porter ses
 * avertissements : ce composant les affiche, y compris — surtout — quand le
 * moteur a refusé de produire un résultat.
 */
export function ListeAvis({ avis, titre }: { avis: readonly Avis[]; titre?: string }) {
  if (avis.length === 0) return null;

  const parNiveau = {
    Blocage: avis.filter((a) => niveauDe(a) === "Blocage"),
    Attention: avis.filter((a) => niveauDe(a) === "Attention"),
    Information: avis.filter((a) => niveauDe(a) === "Information"),
  };

  return (
    <section className="avis">
      {titre && <h3>{titre}</h3>}
      {(["Blocage", "Attention", "Information"] as const).map((niveau) =>
        parNiveau[niveau].length === 0 ? null : (
          <ul key={niveau} className={`avis__liste avis__liste--${niveau.toLowerCase()}`}>
            {parNiveau[niveau].map((a, i) => (
              <li key={`${a.code}-${i}`}>
                <span className="avis__code">{a.code}</span>
                <div>
                  <p className="avis__message">{a.message}</p>
                  {a.detail && <p className="avis__detail">{a.detail}</p>}
                </div>
              </li>
            ))}
          </ul>
        ),
      )}
    </section>
  );
}

/**
 * Écran d'un moteur qui a REFUSÉ de produire un résultat.
 * Le refus n'est jamais muet : il montre exactement pourquoi.
 */
export function Refus({ avis, quoi }: { avis: readonly Avis[]; quoi: string }) {
  const bloquants = avis.filter((a) => niveauDe(a) === "Blocage");

  return (
    <div className="refus">
      <h2>{quoi} n'a pas pu être établi</h2>
      {bloquants.length === 0 ? (
        <p>Aucune donnée exploitable sur la période.</p>
      ) : (
        <ul>
          {bloquants.map((a, i) => (
            <li key={i}>
              <strong>{a.code}</strong> — {a.message}
              {a.detail && <p className="avis__detail">{a.detail}</p>}
            </li>
          ))}
        </ul>
      )}
      <ListeAvis avis={avis.filter((a) => niveauDe(a) !== "Blocage")} titre="Autres observations" />
    </div>
  );
}

export function EtatChargement({ quoi }: { quoi: string }) {
  return <p className="chargement">Calcul de {quoi}…</p>;
}

export function EtatErreur({ erreur }: { erreur: unknown }) {
  const message = erreur instanceof Error ? erreur.message : "Erreur inconnue";
  const motif =
    erreur !== null && typeof erreur === "object" && "motif" in erreur
      ? String((erreur as { motif?: unknown }).motif ?? "")
      : "";

  return (
    <div className="alerte" role="alert">
      <strong>{message}</strong>
      {motif && <p className="avis__detail">{motif}</p>}
    </div>
  );
}
